import * as Sentry from '@sentry/react';
import { LoadingScreen } from 'noddi-ui-web';
import { LanguageCodeType, storage } from 'noddi-util';
import { useEffect, useState } from 'react';
import { LanguageContext } from './LanguageContext';

type LanguageProviderProps = {
  children: React.ReactNode;
  defaultLocale: LanguageCodeType;
  dynamicActivateLocale?: (locale: LanguageCodeType) => Promise<void>;
};

const localeStorageLocaleKey = 'locale';

export const LanguageProvider = ({ children, defaultLocale, dynamicActivateLocale }: LanguageProviderProps) => {
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCodeType>(
    () => (storage.local.getItem<LanguageCodeType>(localeStorageLocaleKey) as LanguageCodeType) || defaultLocale
  );

  // Derive loading from whether the active locale's data has been activated yet,
  // rather than syncing a separate loading flag inside the effect.
  const [activatedLanguage, setActivatedLanguage] = useState<LanguageCodeType | null>(null);
  const loading = Boolean(dynamicActivateLocale) && activatedLanguage !== currentLanguage;

  useEffect(() => {
    if (!dynamicActivateLocale) {
      return;
    }
    dynamicActivateLocale(currentLanguage)
      .then(() => setActivatedLanguage(currentLanguage))
      .catch(() => setActivatedLanguage(currentLanguage));
  }, [currentLanguage, dynamicActivateLocale]);

  // Set the language tag on the <html> element and sentry
  useEffect(() => {
    document.documentElement.setAttribute('lang', currentLanguage);
    Sentry.setTag('language', currentLanguage);
  }, [currentLanguage]);

  return (
    <LanguageContext.Provider value={{ currentLanguage, setCurrentLanguage }}>
      {loading ? <LoadingScreen /> : children}
    </LanguageContext.Provider>
  );
};
